// src/pages/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import { UserIcon, ChartBarIcon, MusicalNoteIcon, UserGroupIcon, HeartIcon, QueueListIcon } from '@heroicons/react/24/outline';
import ArtistCard from '../components/artists/ArtistCard';
import LoadingState from '../components/common/LoadingState';
import ErrorMessage from '../components/common/ErrorMessage';
import { isAuthenticated } from '../services/authService';
import MetricsChart from '../components/charts/MetricsChart';
import { 
  getDashboardSummary, 
  DashboardSummary, 
  AuthError
} from '../services/dashboardService';
import { getArtistDetails, ArtistDetails } from '../services/artistService';
import { getLatest12Months } from '../utils/metricUtils';

// Define all possible metric types
type MetricType = 'score' | 'streams' | 'listeners' | 'saves' | 'playlists';

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<boolean>(false);
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [artistDetails, setArtistDetails] = useState<ArtistDetails | null>(null);
  const [metricType, setMetricType] = useState<MetricType>('score');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setAuthError(false);

        const [dashboardResponse, artistResponse] = await Promise.all([
          getDashboardSummary(),
          getArtistDetails('me')
        ]);

        setDashboardData(dashboardResponse);
        setArtistDetails(artistResponse);
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        if (err instanceof AuthError) {
          setAuthError(true);
        } else {
          setError('Failed to load dashboard data. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <LoadingState message="Loading dashboard data..." />;

  if (error || !dashboardData || !artistDetails) {
    return (
      <ErrorMessage 
        title="Dashboard Data Error" 
        message={error || "Could not load dashboard data."} 
        onRetry={() => window.location.reload()}
      />
    );
  }

  const latestMetrics = getLatest12Months(artistDetails.monthlyMetrics);
  const latest = latestMetrics[latestMetrics.length - 1];
  const previous = latestMetrics.length > 1 ? latestMetrics[latestMetrics.length - 2] : null;

  const scoreChange = latest.score - (previous?.score ?? dashboardData.previousScore);
  const scoreChangePercent = previous?.score
    ? ((scoreChange / previous.score) * 100).toFixed(1)
    : "0.0";
  const isPositiveChange = scoreChange > 0;

  const hasData = Boolean(latestMetrics.length);

  return (
    <div className="container mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-600">Here you can see your score development and compare yourself with similar artists.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-semibold text-gray-700">Your Current Score</h2>
              <div className="flex items-baseline mt-1">
                <span className="text-4xl font-bold text-green-600">{latest.score}</span>
                <span className="text-gray-500 ml-2">/ 100</span>
              </div>
            </div>
            <div className={`flex items-center ${isPositiveChange ? 'text-green-500' : 'text-red-500'}`}>
              {isPositiveChange ? (
                <ArrowUpIcon className="h-5 w-5 mr-1" />
              ) : (
                <ArrowDownIcon className="h-5 w-5 mr-1" />
              )}
              <span className="font-semibold">{Math.abs(scoreChange).toFixed(2)} points</span>
            </div>
          </div>
          <p className="text-gray-500 mt-2">Since last month</p>

          <div className="mt-4 flex justify-between">
            <Link to="/add-metrics" className="text-green-600 hover:text-green-700 text-sm font-medium">
              Enter new metrics →
            </Link>
            <Link to="/artist/me" className="text-green-600 hover:text-green-700 text-sm font-medium">
              View details →
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
          {hasData ? (
            <MetricsChart 
              metrics={latestMetrics}
              initialMetricType={metricType}
              height="250px"
              showControls={true}
            />
          ) : (
            <div className="flex justify-center items-center h-full min-h-64">
              <p className="text-gray-500">No metrics history available</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Engagement Overview</h2>
        {dashboardData.engagement && dashboardData.engagement.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dashboardData.engagement.map((metric, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500">{metric.name}</h3>
                <p className="text-2xl font-bold text-gray-800">
                  {metric.name === 'Save Rate' && typeof metric.value === 'number' 
                    ? metric.value.toFixed(2) 
                    : typeof metric.value === 'number' 
                      ? metric.value.toLocaleString() 
                      : metric.value}
                  {metric.unit && <span className="text-sm ml-1">{metric.unit}</span>}
                </p>
                <span className={`text-sm font-medium ${metric.changePercent >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {metric.changePercent >= 0 ? '+' : ''}{metric.changePercent}%
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500">No engagement metrics available</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">Similar Artists</h2>
          <Link to="/comparisons" className="text-green-600 hover:text-green-700 text-sm font-medium">
            View all →
          </Link>
        </div>
        {dashboardData.similarArtists && dashboardData.similarArtists.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dashboardData.similarArtists.map(artist => (
              <ArtistCard key={artist.id} artist={artist} />
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500">No similar artists found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;